import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useSiteSettings } from './SiteSettingsContext';

export interface BasketItem {
  sku: string;
  name: string;
  price: number;
  imageUrl?: string;
  quantity: number;
}

interface BasketContextType {
  items: BasketItem[];
  itemCount: number;
  total: number;
  addItem: (item: Omit<BasketItem, 'quantity'>, quantity?: number) => void;
  removeItem: (sku: string) => void;
  updateQuantity: (sku: string, quantity: number) => void;
  clearBasket: () => void;
}

const BasketContext = createContext<BasketContextType | undefined>(undefined);

const getStorageKey = (siteNodeId: string | null): string =>
  `basket_${siteNodeId ?? 'default'}`;

const loadBasket = (siteNodeId: string | null): BasketItem[] => {
  try {
    const raw = localStorage.getItem(getStorageKey(siteNodeId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveBasket = (siteNodeId: string | null, items: BasketItem[]): void => {
  try {
    if (items.length === 0) {
      localStorage.removeItem(getStorageKey(siteNodeId));
    } else {
      localStorage.setItem(getStorageKey(siteNodeId), JSON.stringify(items));
    }
  } catch {
    // localStorage full or unavailable — silent fail
  }
};

export const BasketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { settings } = useSiteSettings();
  const [items, setItems] = useState<BasketItem[]>(() => loadBasket(settings.siteNodeId));

  // Re-load basket when siteNodeId changes (e.g. on initial load)
  useEffect(() => {
    setItems(loadBasket(settings.siteNodeId));
  }, [settings.siteNodeId]);

  // Persist to localStorage on change
  useEffect(() => {
    saveBasket(settings.siteNodeId, items);
  }, [items, settings.siteNodeId]);

  const addItem = useCallback((item: Omit<BasketItem, 'quantity'>, quantity = 1) => {
    setItems(prev => {
      const existing = prev.find(i => i.sku === item.sku);
      if (existing) {
        return prev.map(i =>
          i.sku === item.sku
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      }
      return [...prev, { ...item, quantity }];
    });
  }, []);

  const removeItem = useCallback((sku: string) => {
    setItems(prev => prev.filter(i => i.sku !== sku));
  }, []);

  const updateQuantity = useCallback((sku: string, quantity: number) => {
    if (quantity <= 0) {
      setItems(prev => prev.filter(i => i.sku !== sku));
    } else {
      setItems(prev =>
        prev.map(i => (i.sku === sku ? { ...i, quantity } : i))
      );
    }
  }, []);

  const clearBasket = useCallback(() => {
    setItems([]);
  }, []);

  const itemCount = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items]);
  const total = useMemo(() => items.reduce((sum, i) => sum + i.price * i.quantity, 0), [items]);

  const value = useMemo<BasketContextType>(
    () => ({ items, itemCount, total, addItem, removeItem, updateQuantity, clearBasket }),
    [items, itemCount, total, addItem, removeItem, updateQuantity, clearBasket]
  );

  return <BasketContext.Provider value={value}>{children}</BasketContext.Provider>;
};

export const useBasket = (): BasketContextType => {
  const context = useContext(BasketContext);
  if (!context) {
    throw new Error('useBasket must be used within a BasketProvider');
  }
  return context;
};
